import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { User } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { SuspendedNotice, VerifyEmailNotice } from "./AccountNotices";
import DisplaySettings from "./DisplaySettings";
import { useNewRoomReplies } from "../rooms";
import { auth, db } from "../firebase";
import { NotificationsProvider, useNotifications } from "../notifications";
import { BlocksProvider } from "../blocks";
import Window, { Footer } from "./Window";
import Icon, { IconName } from "./Icon";
import Envelope3D from "./Envelope3D";
import GlitterBurst from "./GlitterBurst";
import { MyPlant } from "./Plant";
import SlowDownNotice from "./SlowDownNotice";
import "../styles.css";

// `short` is the label used in the compact phone menu.
const links: { to: string; icon: IconName; label: string; short: string }[] = [
  { to: "/dashboard", icon: "home", label: "Home", short: "Home" },
  { to: "/inbox", icon: "inbox", label: "Inbox", short: "Inbox" },
  { to: "/board", icon: "pin", label: "PenPal Board", short: "Board" },
  { to: "/requests", icon: "request", label: "Requests", short: "Requests" },
  { to: "/address-book", icon: "book", label: "Address Book", short: "Pals" },
  { to: "/community", icon: "community", label: "Community", short: "Rooms" },
  { to: "/rules", icon: "shield", label: "Safety & Rules", short: "Rules" },
  { to: "/profile", icon: "user", label: "My Profile", short: "Profile" },
];

export default function Layout() {
  const [user] = useAuthState(auth);
  return (
    <BlocksProvider uid={user?.uid}>
      <NotificationsProvider uid={user?.uid}>
        <Shell signedIn={!!user} uid={user?.uid} user={user} />
      </NotificationsProvider>
    </BlocksProvider>
  );
}

function Shell({
  signedIn,
  uid,
  user,
}: {
  signedIn: boolean;
  uid?: string;
  user?: User | null;
}) {
  const counts = useNotifications();
  const roomReplies = useNewRoomReplies(uid);
  const badge: Record<string, number> = {
    "/inbox": counts.letters + counts.kudos,
    "/requests": counts.requests,
    "/board": counts.board,
    "/community": roomReplies,
  };

  return (
    <div className="site-shell">
      <header className="banner">
        <div className="banner-top">
          <Link to="/dashboard" className="logo">
            <Icon name="mail" size={37} tone="pink" pop className="mr-1" />
            PenPal Board
            <span className="logo-sub">the 2SLGBTQIA+ pen pal network</span>
          </Link>
          <div className="btn-row">
            <DisplaySettings />
            {signedIn && (
              <button
                onClick={() => auth.signOut()}
                className="btn btn-plain btn-sm"
              >
                <Icon name="logout" size={16} /> Log out
              </button>
            )}
          </div>
        </div>
        <div className="marquee" aria-hidden="true">
          <span className="marquee-track">
            {[0, 1].map((copy) => (
              <span key={copy} className="marquee-copy">
                ★ welcome to the pen pal network ★ black trans lives matter ★ be
                kind ★ write often ★ land back ★ protect each other's privacy ★
                trans rights are human rights ★ QTBIPOC, this space is yours ★
                send kudos generously
              </span>
            ))}
          </span>
        </div>
      </header>

      <MailAlert />
      {uid && <SuspendedNotice uid={uid} />}
      {user && <VerifyEmailNotice user={user} />}
      {uid && <UsernameNudge uid={uid} />}

      <div className="site-grid">
        <aside className="flex flex-col gap-3">
          <Window title="navigation.exe" tone="blue" bodyClassName="!p-2" minimizable={false}>
            <nav className="nav-list" aria-label="Main">
              {links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  <Icon name={link.icon} size={20} pop />
                  <span className="nav-label">{link.label}</span>
                  <span className="nav-label-short" aria-hidden="true">
                    {link.short}
                  </span>
                  {badge[link.to] > 0 && (
                    <span
                      className="nav-count blink-soft"
                      aria-label={`${badge[link.to]} new`}
                    >
                      {badge[link.to]}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
          </Window>

          {uid && (
            <div className="sidebar-plant">
              <MyPlant uid={uid} compact />
            </div>
          )}
        </aside>

        <main className="page">
          <Outlet />
        </main>
      </div>

      <Footer />
      <SlowDownNotice />
    </div>
  );
}

// Older accounts with no username get a reminder until they pick one.
function UsernameNudge({ uid }: { uid: string }) {
  const location = useLocation();
  const [missing, setMissing] = useState(false);

  useEffect(
    () =>
      onSnapshot(
        doc(db, "users", uid),
        (snap) => setMissing(snap.exists() && !snap.data().username),
        () => setMissing(false)
      ),
    [uid]
  );

  if (!missing || location.pathname === "/profile") return null;
  return (
    <p className="notice mx-auto mb-3 flex max-w-5xl flex-wrap items-center gap-2 text-sm" role="status">
      <Icon name="user" size={16} /> Your account doesn't have a username yet, so
      your profile shows "@penpal".
      <Link to="/profile" className="font-bold underline">
        Choose your username
      </Link>
    </p>
  );
}

// When something new arrives, a sealed envelope drops in from the corner.
// Clicking it tips the flap open with a glitter burst, then heads to the mail.
function MailAlert() {
  const { letters, kudos, requests } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const total = letters + kudos + requests;
  // Hidden until something else arrives after it's dismissed.
  const [dismissedTotal, setDismissedTotal] = useState(-1);
  const [opening, setOpening] = useState(false);
  const [burstFrom, setBurstFrom] = useState<{ x: number; y: number } | null>(null);
  const envRef = useRef<HTMLButtonElement>(null);

  const hasMail = letters + kudos > 0;
  const target = hasMail ? "/inbox" : "/requests";
  const visible =
    total > 0 && total !== dismissedTotal && location.pathname !== target;

  const lines = [
    letters > 0 && `${letters} new letter${letters === 1 ? "" : "s"}`,
    kudos > 0 && `${kudos} kudos`,
    requests > 0 && `${requests} pen pal request${requests === 1 ? "" : "s"}`,
  ].filter(Boolean) as string[];

  const open = () => {
    const rect = envRef.current?.getBoundingClientRect();
    if (rect) {
      setBurstFrom({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 3 });
    }
    setOpening(true);
    window.setTimeout(() => {
      setOpening(false);
      navigate(target);
    }, 1100);
  };

  return (
    <>
      {burstFrom && (
        <GlitterBurst origin={burstFrom} onDone={() => setBurstFrom(null)} />
      )}
      {visible && (
        <div className="mail-pop" role="alert">
          <div className="window-title">
            <span className="window-title-text">
              <span className="blink-soft">●</span> new_mail.alert
            </span>
            <button
              className="alert-close"
              onClick={() => setDismissedTotal(total)}
              aria-label="Dismiss until something new arrives"
            >
              <Icon name="close" size={12} />
            </button>
          </div>
          <div className="mail-pop-body">
            <button
              ref={envRef}
              type="button"
              className="env-button"
              onClick={open}
              disabled={opening}
              aria-label="Open your mail"
            >
              <Envelope3D open={opening} width={150} tone={hasMail ? "pink" : "blue"}>
                <Icon name={hasMail ? "heart" : "request"} size={20} tone="pink" pop />
                <span className="font-pixel text-[0.6rem]">for you!</span>
              </Envelope3D>
            </button>
            <p className="alert-heading">
              {hasMail ? "You've got mail!" : "A pen pal request!"}
            </p>
            <p className="text-sm">{lines.join(" · ")}</p>
            <p className="blink-soft font-pixel text-[0.65rem]">
              click the envelope
            </p>
          </div>
        </div>
      )}
    </>
  );
}
